"use client";

import { useCallback, useEffect, useState } from "react";

type Window = {
  checks: number;
  passed: number;
  availability_pct: number | null;
  observed: boolean;
  mean_latency_ms: number | null;
};

type Component = {
  id: string;
  name: string;
  path_prefix: string;
  state: "operational" | "down" | "unobserved";
  last_checked_at: string | null;
  last_latency_ms: number | null;
  observed_since: string | null;
  availability: Record<string, Window>;
};

type Status = {
  generated_at: string;
  overall: "operational" | "degraded" | "major_outage" | "partially_observed" | "unobserved";
  components: Component[];
  method: { statement: string; probe_interval_seconds: number; windows: string[] };
  uptime_commitment: { statement: string };
  incidents: { statement: string };
};

export const STATUS_URL = "/api/devtools/status";

const OVERALL: Record<Status["overall"], { label: string; colour: string }> = {
  operational: { label: "All components operational", colour: "#1a7f37" },
  degraded: { label: "One component is down", colour: "#b35900" },
  major_outage: { label: "More than one component is down", colour: "#c62828" },
  partially_observed: { label: "Some components have no recent health check", colour: "#5f6b7a" },
  unobserved: { label: "No recent health checks recorded", colour: "#5f6b7a" },
};

const STATE: Record<Component["state"], { label: string; colour: string }> = {
  operational: { label: "Operational", colour: "#1a7f37" },
  down: { label: "Down", colour: "#c62828" },
  unobserved: { label: "Not observed", colour: "#5f6b7a" },
};

/**
 * True when no component has ever been checked: the prober has not written a
 * single row yet (it is starting, or it is not running). That is a different
 * fact from "checks stopped", and the page says which one it is rather than
 * printing "Not observed" in every cell.
 */
export function monitoringNotStarted(s: Status): boolean {
  return s.components.length > 0 && s.components.every((c) => !c.observed_since && !c.last_checked_at);
}

function pct(w: Window | undefined): string {
  if (!w || !w.observed || w.availability_pct === null) return "Not observed";
  return `${w.availability_pct.toFixed(2)}% (${w.passed} of ${w.checks} checks)`;
}

function when(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
}

export default function StatusView() {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const resp = await fetch(STATUS_URL, { cache: "no-store" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setStatus((await resp.json()) as Status);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "request failed");
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60_000);
    return () => clearInterval(timer);
  }, [load]);

  const overall = status ? OVERALL[status.overall] ?? OVERALL.unobserved : null;
  const notStarted = status ? monitoringNotStarted(status) : false;

  return (
    <main style={{ minHeight: "100vh", background: "#f7f8fa", color: "#1b1f24", fontFamily: "system-ui, -apple-system, Segoe UI, sans-serif" }}>
      <div style={{ maxWidth: 880, margin: "0 auto", padding: "32px 16px" }}>
        <p style={{ margin: 0, fontSize: 13, color: "#5f6b7a" }}>
          <a href="https://www.ampverve.com/api-docs/" style={{ color: "#0b5cad" }}>AmpVerve API documentation</a>
        </p>
        <h1 style={{ fontSize: 28, margin: "8px 0 4px" }}>API status</h1>
        <p style={{ margin: "0 0 24px", color: "#5f6b7a" }}>
          Health of the public API components, counted from recorded health checks. Refreshes every minute.
        </p>

        {error && !status && (
          <div role="alert" style={{ padding: 16, border: "1px solid #c62828", borderRadius: 8, background: "#fff" }}>
            <strong>Status temporarily unavailable.</strong> The status service did not answer ({error}). That can itself
            mean the Partner API is down. The raw answer is at <code>{STATUS_URL}</code>.
          </div>
        )}

        {!status && !error && <p>Loading the latest health checks.</p>}

        {status && notStarted && (
          <>
            <div style={{ padding: 16, borderRadius: 8, background: "#fff", borderLeft: "6px solid #0b5cad", marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: "#0b5cad" }}>Monitoring is starting</div>
              <p style={{ fontSize: 14, margin: "8px 0 0" }}>
                No health checks have been recorded yet, so there is no current state and no availability figure to
                show. This page does not fill the gap with an assumed figure. Results appear here after the first
                check; each component is then checked every {status.method.probe_interval_seconds} seconds.
              </p>
              <div style={{ fontSize: 13, color: "#5f6b7a", marginTop: 8 }}>
                As of {when(status.generated_at)}
                {error ? `. The last refresh failed (${error}); this is the previous answer.` : ""}
              </div>
            </div>

            <h2 style={{ fontSize: 18 }}>Components that will be monitored</h2>
            <ul style={{ background: "#fff", borderRadius: 8, padding: "12px 12px 12px 32px", fontSize: 14 }}>
              {status.components.map((c) => (
                <li key={c.id} style={{ padding: "4px 0" }}>
                  {c.name} <code style={{ fontSize: 12, color: "#5f6b7a" }}>{c.path_prefix}</code>
                </li>
              ))}
            </ul>

            <h2 style={{ fontSize: 18, marginTop: 28 }}>How this is measured</h2>
            <p style={{ fontSize: 14 }}>{status.method.statement}</p>
            <p style={{ fontSize: 14 }}>{status.uptime_commitment.statement}</p>
            <p style={{ fontSize: 14 }}>{status.incidents.statement}</p>
          </>
        )}

        {status && overall && !notStarted && (
          <>
            <div style={{ padding: 16, borderRadius: 8, background: "#fff", borderLeft: `6px solid ${overall.colour}`, marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: overall.colour }}>{overall.label}</div>
              <div style={{ fontSize: 13, color: "#5f6b7a", marginTop: 4 }}>
                As of {when(status.generated_at)}
                {error ? `. The last refresh failed (${error}); this is the previous answer.` : ""}
              </div>
            </div>

            <div style={{ overflowX: "auto", background: "#fff", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: "1px solid #e1e4e8" }}>
                    <th style={{ padding: 10 }}>Component</th>
                    <th style={{ padding: 10 }}>Now</th>
                    {status.method.windows.map((w) => (
                      <th key={w} style={{ padding: 10 }}>{w}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {status.components.map((c) => {
                    const s = STATE[c.state] ?? STATE.unobserved;
                    return (
                      <tr key={c.id} style={{ borderBottom: "1px solid #f0f1f3", verticalAlign: "top" }}>
                        <td style={{ padding: 10 }}>
                          <div style={{ fontWeight: 600 }}>{c.name}</div>
                          <div style={{ fontSize: 12, color: "#5f6b7a" }}><code>{c.path_prefix}</code></div>
                        </td>
                        <td style={{ padding: 10 }}>
                          <span style={{ color: s.colour, fontWeight: 600 }}>{s.label}</span>
                          <div style={{ fontSize: 12, color: "#5f6b7a" }}>
                            Checked {when(c.last_checked_at)}
                            {c.last_latency_ms !== null ? `, ${Math.round(c.last_latency_ms)} ms` : ""}
                          </div>
                        </td>
                        {status.method.windows.map((w) => (
                          <td key={w} style={{ padding: 10, fontSize: 13 }}>{pct(c.availability[w])}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <h2 style={{ fontSize: 18, marginTop: 28 }}>How this is measured</h2>
            <p style={{ fontSize: 14 }}>{status.method.statement}</p>
            <p style={{ fontSize: 14 }}>
              Each service is checked every {status.method.probe_interval_seconds} seconds. {status.uptime_commitment.statement}
            </p>
            <p style={{ fontSize: 14 }}>{status.incidents.statement}</p>
          </>
        )}
      </div>
    </main>
  );
}
